import { useState } from 'react';
import { Download } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { ErrorCode, PreflightCopy } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import {
  useGenerateWholeReport,
  useDownloadReportCover,
  useFilingPreflight,
  GenerateWholeReportError,
} from '@/hooks/queries/useFinanceReports';
import { triggerBrowserDownload } from './downloadHelpers';
import { HardFailureList } from './HardFailureList';
import { PortalActionList } from './PortalActionList';
import { withReceivingOffice } from './receivingOffice';
import { useCampaignStore } from '@/stores';

/** Route to the finance settings page where the State Filer ID is entered (the onboarding/setup form). */
const FINANCE_SETTINGS_ROUTE = '/admin/finance/settings/setup';

interface GenerateWholeReportButtonProps {
  reportId: string;
  /**
   * Visual prominence. For a whole-report portal-upload jurisdiction (VA) generating the
   * document is the recommended action, so the caller promotes it to `'primary'` (filled).
   */
  variant?: 'primary' | 'secondary';
  /**
   * #313 — the filing-TRANSPORT axis (from `usePortalUploadModel.isEmailFiling`). True for a
   * jurisdiction that files by EMAIL (NC, MS — no upload portal). When true the post-generate line
   * says "email … to {office}" instead of "upload … to the portal".
   */
  isEmailFiling?: boolean;
  /** The served receiving-office name (`profile.portalName` — "NCSBE", "Secretary of State"). */
  officeName?: string;
  /**
   * The declared `separateSignedCover` axis (ADR-054 — NC's two-artifact filing): surfaces the
   * signable CRO-1000 cover next to the generated file. Never for Mississippi, whose PDF is the filing.
   */
  separateSignedCover?: boolean;
}

/**
 * "Generate report" for a whole-report portal-upload jurisdiction (VA COMET, #126's
 * non-finalizing generate seam). Builds and downloads the full report XML WITHOUT
 * finalizing — no `Confirmed → Reported` flip, no `Status = Filed`, no cash freeze
 * (generate ≠ file, ADR-019). Repeatable and free: the treasurer downloads it, uploads
 * it to the state portal, then records the filing via "Mark filed in portal".
 *
 * #313 — North Carolina reaches this same seam (whole-report + upload ≠ file) but files by EMAIL,
 * not a portal upload, so when `isEmailFiling` the success copy is worded around emailing NCSBE and
 * the signable CRO-1000 cover download is surfaced next to the `.cfd`. A missing State Filer ID is
 * surfaced as an actionable config error with a Finance Settings link (keyed off the server's stable
 * error code, not the message text).
 *
 * Distinct from `FilePackageButton` (federal — generate = file, finalizes) and
 * `ExportDeltaButton` (AL — incremental append-only delta). Rendered only inside the
 * whole-report portal-upload branch, so it does not self-gate on the serving model.
 *
 * #137 — gates on preflight HARD blockers: the generate seam is non-finalizing + re-runnable,
 * so without this it would happily emit a file the portal rejects on upload (negative cash,
 * a blank required contributor/payee address). Blockers are surfaced up front and disable the
 * button; SOFT warnings (missing employer/occupation) never block. The server enforces the
 * same refusal (defense-in-depth), surfaced here if the button is somehow clicked while stale.
 */
export function GenerateWholeReportButton({
  reportId,
  variant = 'primary',
  isEmailFiling = false,
  officeName = 'receiving office',
  separateSignedCover = false,
}: GenerateWholeReportButtonProps) {
  const { generateWholeReport } = useGenerateWholeReport();
  const { downloadCover } = useDownloadReportCover();
  const { data: preflight } = useFilingPreflight(reportId);
  const [busy, setBusy] = useState(false);
  // Structured so the config-error affordance can branch on the server's stable error CODE
  // (MissingStateFilerId → Finance Settings link) without parsing the human message (#313).
  const [error, setError] = useState<{ message: string; code: string | null } | null>(null);
  // #216 — the download is silent, so the treasurer had no signal it worked. Confirm success
  // inline and name the downloaded file so they know exactly what to upload/email.
  const [downloadedFileName, setDownloadedFileName] = useState<string | null>(null);
  // A zip is a multi-file package (New Jersey: a hand-entry list plus the files it says to upload),
  // not one file to upload as-is.
  const [downloadedPackage, setDownloadedPackage] = useState(false);
  // ADR-054 — the signable CRO-1000 cover (NC files two artifacts: the .cfd + the cover).
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverError, setCoverError] = useState<string | null>(null);
  // The server refuses generate below Finance Manage; a view-only user gets no control to click.
  const canManage = useCampaignStore((st) => st.canManage('Finance'));
  if (!canManage) return null;

  const hardFailures = preflight?.hardFailures ?? [];
  const hasBlockers = hardFailures.length > 0;

  const handleGenerate = async () => {
    setBusy(true);
    setError(null);
    setCoverError(null);
    setDownloadedFileName(null);
    try {
      const result = await generateWholeReport(reportId);
      triggerBrowserDownload(result.fileBlob, result.fileName);
      setDownloadedFileName(result.fileName);
      setDownloadedPackage(result.fileBlob.type === 'application/zip');
    } catch (e) {
      if (e instanceof GenerateWholeReportError) {
        setError({ message: e.message, code: e.code });
      } else {
        setError({
          message: e instanceof Error ? e.message : 'Failed to generate the report',
          code: null,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadCover = async () => {
    setCoverBusy(true);
    setCoverError(null);
    try {
      const result = await downloadCover(reportId);
      triggerBrowserDownload(result.blob, result.fileName);
    } catch (e) {
      setCoverError(e instanceof Error ? e.message : 'Failed to download the cover');
    } finally {
      setCoverBusy(false);
    }
  };

  const isConfigError = error?.code === ErrorCode.MissingStateFilerId;

  return (
    <div className="space-y-2">
      <HardFailureList hardFailures={hardFailures} />
      <PortalActionList warnings={preflight?.warnings ?? []} />
      <Button
        variant={variant === 'primary' ? 'default' : 'outline'}
        onClick={handleGenerate}
        disabled={busy || hasBlockers}
        data-testid="generate-whole-report-button"
      >
        <Download className="mr-1 h-4 w-4" />
        {busy ? 'Generating…' : 'Generate report'}
      </Button>
      {hasBlockers && (
        <p
          className="text-sm text-muted-foreground"
          data-testid="generate-whole-report-blocked-note"
        >
          The portal would reject this report as-is. Fix the blocking issue
          {hardFailures.length === 1 ? '' : 's'} listed above, then generate.
        </p>
      )}
      {downloadedFileName && !error && (
        <p className="text-sm text-green-700" data-testid="generate-whole-report-success">
          Report generated — <span className="font-medium">{downloadedFileName}</span> downloaded.{' '}
          {isEmailFiling
            ? withReceivingOffice(
                downloadedFileName.endsWith('.zip')
                  ? PreflightCopy.GenerateEmailPackageSuccessAction
                  : PreflightCopy.GenerateEmailSuccessAction,
                officeName,
              )
            : downloadedPackage
              ? 'The download holds several files. Open the hand-entry list in it and follow its steps, then come back and mark it filed.'
              : 'Upload this file to the portal, then come back and mark it filed.'}
        </p>
      )}
      {/* ADR-054 — NC files two artifacts; surface the signable cover the moment the .cfd is
          generated so the treasurer can download, sign, and deliver it to NCSBE. */}
      {separateSignedCover && downloadedFileName && !error && (
        <div className="space-y-1" data-testid="generate-whole-report-cover">
          <Button
            variant="outline"
            onClick={handleDownloadCover}
            disabled={coverBusy}
            data-testid="generate-whole-report-cover-button"
          >
            <Download className="mr-1 h-4 w-4" />
            {coverBusy ? 'Downloading…' : 'Download CRO-1000 cover'}
          </Button>
          <p className="text-sm text-muted-foreground">{PreflightCopy.SuccessPanelNcCoverStep}</p>
          {coverError && (
            <p className="text-sm text-destructive" data-testid="generate-whole-report-cover-error">
              {coverError}
            </p>
          )}
        </div>
      )}
      {error &&
        (isConfigError ? (
          <div
            className="text-sm text-destructive space-y-1"
            data-testid="generate-whole-report-config-error"
          >
            <p>{error.message}</p>
            <p>
              <Link
                to={FINANCE_SETTINGS_ROUTE}
                className="font-medium underline underline-offset-2"
                data-testid="generate-whole-report-settings-link"
              >
                Go to Finance Settings
              </Link>{' '}
              to add it, then generate again.
            </p>
          </div>
        ) : (
          <p className="text-sm text-destructive" data-testid="generate-whole-report-error">
            {error.message}
          </p>
        ))}
    </div>
  );
}
